import React from 'react';
import { Link } from 'react-router-dom';

// Helper function for smooth scrolling
const scrollToSection = (id: string) => {
  // First check if we're on the homepage
  const isHomePage = window.location.pathname === '/';
  
  if (isHomePage) {
    // We're on the homepage, scroll to the section
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  } else {
    // We're not on the homepage, redirect to homepage with hash
    window.location.href = `/#${id}`;
  }
};

const Footer: React.FC = () => {
  return (
    <footer className="bg-tuktuk-brown text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Restaurant Info */}
          <div className="flex flex-col items-center md:items-start">
            <div className="flex items-center mb-4">
              <img 
                src={`${process.env.PUBLIC_URL}/images/cambodia-temple.png`}
                alt="Tuk Tuk Restaurant Logo"
                className="h-10 mr-3"
              />
              <h3 className="text-xl font-display font-bold">Tuk Tuk Restaurant</h3>
            </div>
            <p className="mb-2">Sivatha Boulevard</p>
            <p className="mb-2">Siem Reap, Cambodia</p>
            <p className="mb-2">Phone: +81 70-4476-1574</p>
            <p className="mb-2">WhatsApp: +81 70-4476-1574</p>
            <p className="mb-2">Email: info@tuktukrestaurant.com</p>
          </div>

          {/* Opening Hours */}
          <div>
            <h3 className="text-xl font-display font-bold mb-4">Opening Hours</h3>
            <p className="mb-2">Monday - Friday: 10:00 - 22:00</p>
            <p className="mb-2">Saturday - Sunday: 10:00 - 22:00</p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-display font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="hover:text-tuktuk-beige transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <button onClick={() => scrollToSection('about')} className="hover:text-tuktuk-beige transition-colors text-white bg-transparent border-none p-0 cursor-pointer text-left">
                  About Us
                </button>
              </li>
              <li>
                <Link to="/menu" className="hover:text-tuktuk-beige transition-colors">
                  Menu
                </Link>
              </li>
              <li>
                <button onClick={() => scrollToSection('farm')} className="hover:text-tuktuk-beige transition-colors text-white bg-transparent border-none p-0 cursor-pointer text-left">
                  Farm to Table
                </button>
              </li>
              <li>
                <Link to="/farm-tours" className="hover:text-tuktuk-beige transition-colors">
                  Farm Tours
                </Link>
              </li>
              <li>
                <Link to="/reservations" className="hover:text-tuktuk-beige transition-colors">
                  Reservations
                </Link>
              </li>
              <li>
                <button onClick={() => scrollToSection('rental')} className="hover:text-tuktuk-beige transition-colors text-white bg-transparent border-none p-0 cursor-pointer text-left">
                  Bike & Scooter Rental
                </button>
              </li>
              <li>
                <Link to="/contact" className="hover:text-tuktuk-beige transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="text-xl font-display font-bold mb-4">Follow Us</h3>
            <div className="flex space-x-4">
              <a
                href="https://facebook.com/profile.php?id=61566688302073"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-tuktuk-beige transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
                Facebook
              </a>
            </div>
            <div className="mt-4">
              <h4 className="font-semibold mb-2">Direct Reservation</h4>
              <a
                href="https://wa.me/817044761574"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-tuktuk-beige transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-tuktuk-beige border-opacity-20 mt-8 pt-8 text-center">
          <div className="flex justify-center mb-4">
            <img src={`${process.env.PUBLIC_URL}/images/tuk-tuk-cambodia.png`} alt="Tuk Tuk" className="h-8 opacity-70" />
          </div>
          <p>&copy; {new Date().getFullYear()} Tuk Tuk Restaurant. All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 